import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import axios from 'axios';

export function ForgotPassword(): JSX.Element {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await axios.post('/api/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm card-paper p-6 sm:p-8 space-y-6">
        <header className="space-y-1">
          <h1 className="font-sans text-3xl">Reset your password</h1>
          <p className="text-sm text-muted-foreground">
            Enter your account email and we&rsquo;ll send you a reset link.
          </p>
        </header>

        {sent ? (
          <div className="space-y-4">
            <p className="card-paper px-3 py-2.5 text-sm" role="status">
              If that account exists, we&rsquo;ve sent a password reset link. Check your inbox.
            </p>
            <Link to="/login" className="text-sm underline underline-offset-4">Back to sign in</Link>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-1.5">
              <label htmlFor="fp-email" className="field-label">Email</label>
              <input
                id="fp-email"
                className="input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
            <button type="submit" disabled={pending} className="btn-primary w-full">
              {pending ? 'Sending…' : 'Send reset link'}
            </button>
            <Link to="/login" className="block text-center text-sm text-muted-foreground underline underline-offset-4">
              Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
